import { useCallback, useState } from 'react'

import { asFailure, type Failure } from '../../api/request'
import {
  type Calendar,
  type TakeOver,
  changeCalendar,
  fetchCalendars,
  readCalendar,
  subscribeToCalendar,
  unsubscribeFromCalendar,
} from '../../api/timetable'
import { useLoad } from '../../api/useLoad'
import { Button } from '../../ui/Control'
import { Failed, Waiting } from '../../ui/Status'

/**
 * The calendars this space subscribes to.
 *
 * **An address, not a file.** A timetable changes during a term — a room, a substitution, a week
 * moved — so a calendar read once is wrong by the second week and nothing notices. What is kept here
 * is where the calendar lives; a run in the background reads it a few times a day, and what came of
 * the last reading stands beside it.
 *
 * That last part is not decoration. A subscription that has quietly stopped working is worse than no
 * subscription at all: the plan simply stops changing and goes on looking right. So every calendar
 * says when it was last read, what came of it, and what went wrong if something did.
 */
export interface CalendarsProps {
  readonly onChanged: () => void
  readonly onClose: () => void
}

export function Calendars({ onChanged, onClose }: CalendarsProps) {
  const list = useLoad(useCallback((signal: AbortSignal) => fetchCalendars(signal), []))
  const [refused, setRefused] = useState<Failure | null>(null)
  const [busy, setBusy] = useState(false)
  const [name, setName] = useState('')
  const [url, setUrl] = useState('')
  const [read, setRead] = useState<TakeOver | null>(null)

  const calendars = list.load.status === 'ready' ? list.load.value : []
  const ready = name.trim() !== '' && url.trim() !== ''

  const attempt = async (work: () => Promise<void>) => {
    setBusy(true)
    try {
      setRefused(null)
      await work()
    } catch (error) {
      setRefused(asFailure(error))
    } finally {
      setBusy(false)
    }
  }

  return (
    <article
      className="sheet sheet--calendars"
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          onClose()
        }
      }}
    >
      <header className="sheet__head">
        <Button
          tone="bare"
          className="sheet__close"
          icon="close"
          iconOnlyLabel="Close"
          onClick={onClose}
        />
        <h2 className="sheet__title-text">Calendars</h2>
        <p className="sheet__facts">
          <span>
            ICS addresses MyLib reads for you — the one ILIAS hands out under /calendar.php, for
            instance.
          </span>
        </p>
      </header>

      <div className="appointment-detail">
        <form
          className="calendars__add"
          onSubmit={(event) => {
            event.preventDefault()
            if (!ready) {
              return
            }
            void attempt(async () => {
              await subscribeToCalendar(name, url)
              setName('')
              setUrl('')
              list.retry()
              onChanged()
            })
          }}
        >
          <div className="appointment-detail__field">
            <label className="appointment-detail__label" htmlFor="calendar-name">
              Name
            </label>
            <input
              id="calendar-name"
              className="appointment-detail__text"
              value={name}
              placeholder="ILIAS"
              onChange={(event) => {
                setName(event.target.value)
              }}
            />
          </div>
          <div className="appointment-detail__field">
            <label className="appointment-detail__label" htmlFor="calendar-url">
              Address
            </label>
            <input
              id="calendar-url"
              className="appointment-detail__text"
              value={url}
              placeholder="https://…/calendar.php?client_id=…"
              onChange={(event) => {
                setUrl(event.target.value)
              }}
            />
          </div>
          <div className="controls">
            <Button tone="primary" type="submit" disabled={!ready || busy}>
              Subscribe
            </Button>
          </div>
          <p className="appointment-detail__aside">
            MyLib reads it once straight away, so an address that is not a calendar is refused
            before anything is kept. Afterwards it is read again a few times a day.
          </p>
        </form>

        {refused !== null && <Failed failure={refused} />}
        {busy && <Waiting label="Reading the calendar." />}

        {list.load.status === 'failed' && (
          <Failed
            failure={list.load.failure}
            onRetry={list.retry}
            title="The calendars did not load"
          />
        )}

        {calendars.length > 0 && (
          <ul className="calendars">
            {calendars.map((calendar) => (
              <Subscribed
                key={calendar.id}
                calendar={calendar}
                busy={busy}
                onRename={(wantedName, wantedUrl) => {
                  void attempt(async () => {
                    await changeCalendar(calendar.id, wantedName, wantedUrl)
                    list.retry()
                  })
                }}
                onRead={() => {
                  void attempt(async () => {
                    setRead(await readCalendar(calendar.id))
                    list.retry()
                    onChanged()
                  })
                }}
                onRemove={() => {
                  void attempt(async () => {
                    await unsubscribeFromCalendar(calendar.id)
                    list.retry()
                  })
                }}
              />
            ))}
          </ul>
        )}

        {read !== null && (
          <p className="appointment-detail__aside">
            {`${String(read.added.length)} new, ${String(read.changed.length)} changed, ${String(read.untouched.length)} unchanged.`}
            {read.notes.map((note) => (
              <span className="calendars__note" key={note}>
                {note}
              </span>
            ))}
          </p>
        )}
      </div>
    </article>
  )
}

function Subscribed({
  calendar,
  busy,
  onRename,
  onRead,
  onRemove,
}: {
  readonly calendar: Calendar
  readonly busy: boolean
  readonly onRename: (name: string, url: string) => void
  readonly onRead: () => void
  readonly onRemove: () => void
}) {
  const [name, setName] = useState(calendar.name)
  const [url, setUrl] = useState(calendar.url)
  const [confirming, setConfirming] = useState(false)

  const save = () => {
    if (
      name.trim() !== '' &&
      url.trim() !== '' &&
      (name !== calendar.name || url !== calendar.url)
    ) {
      onRename(name, url)
    }
  }

  return (
    <li className="calendars__one" data-failing={calendar.lastError !== ''}>
      <div className="appointment-detail__field">
        <label className="visually-hidden" htmlFor={`name-${calendar.id}`}>
          Name of this calendar
        </label>
        <input
          id={`name-${calendar.id}`}
          className="appointment-detail__text"
          value={name}
          onChange={(event) => {
            setName(event.target.value)
          }}
          onBlur={save}
        />
      </div>
      <div className="appointment-detail__field">
        <label className="visually-hidden" htmlFor={`url-${calendar.id}`}>
          Address of this calendar
        </label>
        <input
          id={`url-${calendar.id}`}
          className="appointment-detail__text calendars__url"
          value={url}
          onChange={(event) => {
            setUrl(event.target.value)
          }}
          onBlur={save}
        />
      </div>

      <p className="calendars__state">
        {calendar.lastReadAt === null
          ? 'Not read yet.'
          : `Last read ${new Date(calendar.lastReadAt).toLocaleString()}${calendar.lastResult === '' ? '' : ` — ${calendar.lastResult}`}.`}
        {calendar.lastError !== '' && (
          <span className="calendars__error">{calendar.lastError}</span>
        )}
      </p>

      <div className="controls controls--tight">
        <Button tone="quiet" disabled={busy} onClick={onRead}>
          Read it now
        </Button>
        {confirming ? (
          <>
            <Button tone="quiet" className="control--danger" onClick={onRemove}>
              Stop subscribing
            </Button>
            <Button
              tone="bare"
              onClick={() => {
                setConfirming(false)
              }}
            >
              Keep it
            </Button>
          </>
        ) : (
          <Button
            tone="bare"
            onClick={() => {
              setConfirming(true)
            }}
          >
            Remove
          </Button>
        )}
      </div>
      {confirming && <p className="appointment-detail__aside">Its appointments stay.</p>}
    </li>
  )
}
